import React, { useEffect, useState } from 'react';
import { sp, API } from './sponsorApi';
import { FileText, Upload, Download, Trash2, ScrollText, Loader2 } from 'lucide-react';

const CATEGORIES = ['GENERAL', 'CONVENTION', 'FACTURE', 'JUSTIFICATIF', 'RAPPORT', 'AUTRE'];

export default function ConventionDocumentsTab({ role }) {
  const [convention, setConvention] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('GENERAL');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([sp.get('/convention'), sp.get('/documents')]);
      setConvention(c.data.convention);
      setDocs(d.data.documents || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file || !title) return;
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('title', title); fd.append('category', category);
      await sp.upload('/documents', fd);
      setMsg('Document ajouté.'); setTitle(''); setFile(null);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec du téléversement.'); }
    finally { setBusy(false); }
  };

  const openTempLink = async (id) => {
    try {
      const r = await sp.post(`/documents/${id}/link`, {});
      const a = document.createElement('a');
      a.href = `${API}${r.data.download_url}`;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch { setMsg('Lien indisponible.'); }
  };

  const fmtSize = (n) => (n >= 1024 ? `${(n / 1024).toFixed(1)} Ko` : `${n} o`);

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await sp.del(`/documents/${id}`); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Suppression impossible.'); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-8" data-testid="convention-documents-tab">
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 text-emerald-400 mb-4">
          <ScrollText className="w-5 h-5" /><h2 className="text-white font-semibold">Convention de partenariat</h2>
        </div>
        {!convention && (
          <p className="text-slate-400 text-sm" data-testid="convention-empty">
            Aucune convention active. O'SCOP formalisera votre convention de partenariat ; elle apparaîtra ici.
          </p>
        )}
        {convention && (
          <div className="space-y-4" data-testid="convention-content">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Référence" value={convention.reference} />
              <Field label="Type" value={convention.engagement_type} />
              <Field label="Début" value={convention.start_date || '—'} />
              <Field label="Fin" value={convention.end_date || '—'} />
              <Field label="Statut" value={convention.status} />
              <Field label="Montant" value={convention.amount_total ? `${convention.amount_total} €` : '—'} />
            </div>
            <p className="text-white font-medium">{convention.title}</p>
            {(convention.clauses || []).length > 0 && (
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Clauses</p>
                <ul className="space-y-2">
                  {convention.clauses.map((cl, i) => (
                    <li key={i} className="bg-slate-800/60 rounded-lg p-3 text-sm">
                      <span className="text-emerald-300 font-medium">{cl.titre}</span>
                      <p className="text-slate-300 mt-1">{cl.contenu}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(convention.counterparts || []).length > 0 && (
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Contreparties convenues</p>
                <ul className="list-disc list-inside text-slate-300 text-sm space-y-1">
                  {convention.counterparts.map((c, i) => (<li key={i}><b className="text-white">{c.label}</b> — {c.description}</li>))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 text-emerald-400 mb-4">
          <FileText className="w-5 h-5" /><h2 className="text-white font-semibold">Centre documentaire privé</h2>
        </div>
        <p className="text-slate-500 text-xs mb-4">Documents confidentiels — téléchargement par lien temporaire uniquement (aucune URL publique permanente).</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du document"
            data-testid="doc-title-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-emerald-500" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select"
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-emerald-500">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input"
            className="text-slate-300 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white" />
        </div>
        <button onClick={upload} disabled={busy || !file || !title} data-testid="doc-upload-btn"
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
          <Upload className="w-4 h-4" />{busy ? 'Envoi…' : 'Téléverser'}
        </button>
        {msg && <p className="text-sm text-emerald-400 mt-2" data-testid="doc-msg">{msg}</p>}

        <ul className="mt-5 space-y-2">
          {docs.length === 0 && <li className="text-slate-500 text-sm" data-testid="docs-empty">Aucun document.</li>}
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 text-sm" data-testid={`doc-${d.id}`}>
              <div>
                <span className="text-white">{d.title}</span>
                <span className="text-slate-500 ml-2 text-xs">{d.category} · {d.filename} · {fmtSize(d.size)}</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => openTempLink(d.id)} data-testid={`doc-download-${d.id}`} className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><Download className="w-4 h-4" />Lien</button>
                {role === 'sponsor_owner' && (
                  <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Field({ label, value }) {
  return (<div><p className="text-slate-500 text-xs">{label}</p><p className="text-white font-medium">{value}</p></div>);
}
function Loading() {
  return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;
}
